import { Suspense, lazy, ElementType } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import MainLayout from '../layouts/main';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import AuthLayout from '../layouts/auth/AuthLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import CountryFeatureGuard from '../guards/CountryFeatureGuard';
import ClientMessagesGuard from '../guards/ClientMessagesGuard';
import RegistrationCompletionGuard from '../guards/RegistrationCompletionGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { DEFAULT_PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
import { ClientPermission, COUNTRY_SPECIFIC_FEATURES, UserRole } from '../enums/shared.enums';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import { ADMIN_ROLES, PAYER_ROLES } from '../constants/common.constants';
import { ROLE_PERMISSION_MAP } from '../constants/shared.constants';
// admin components
import FaxManagement from '../components/admin/FaxManagement';
import { ProviderKnowledgeBase } from 'src/pages/main/ProviderKnowledgeBase';
import StripeRedirectModal from '../components/user/StripeRedirectModal';
import { useHealthStatusMessagePoller } from 'src/utils/useHealthStatusPoller';

const Loadable = (Component: ElementType) => {
    const LoadableComponent = (props: any) => {
        const { pathname } = useLocation();

        const suppressFallback = pathname.includes('/main') || pathname.includes('/auth');

        return (
            <Suspense fallback={suppressFallback ? null : <LoadingScreen />}>
                <Component {...props} />
            </Suspense>
        );
    };

    return LoadableComponent;
};

export default function Router() {
    useHealthStatusMessagePoller();

    return useRoutes([
        // Auth Routes
        {
            path: 'auth',
            element: <AuthLayout />,
            children: [
                {
                    path: 'login',
                    element: (
                        <GuestGuard>
                            <Login />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'admin-login',
                    element: (
                        <GuestGuard>
                            <Login />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'register/:source',
                    element: (
                        <GuestGuard>
                            <Register />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'register',
                    element: (
                        <GuestGuard>
                            <Register />
                        </GuestGuard>
                    ),
                },
                { path: 'verify', element: <VerifyCode /> },
                {
                    path: 'provider-register',
                    element: (
                        <GuestGuard>
                            <ProviderRegister />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'provider-register/:source',
                    element: (
                        <GuestGuard>
                            <ProviderRegister />
                        </GuestGuard>
                    ),
                },
            ],
        },

        // Magic Link Routes
        {
            path: 'ml',
            children: [
                {
                    path: 'minor-consent',
                    element: <MinorConsent />,
                },
                {
                    path: 'email-verification',
                    element: <EmailVerification />,
                },
                {
                    path: 'appointment-request',
                    element: <AppointmentRequestConfirmation />,
                },
                {
                    path: 'appointment-reschedule-request',
                    element: <AppointmentRescheduleRequestConfirmation />,
                },
                {
                    path: 'new-assessment',
                    element: <NewAssessment />,
                },
                {
                    path: 'app-download/:userId',
                    element: <AppDownload />,
                },
                {
                    path: 'app-download',
                    element: <AppDownload />,
                },
                {
                    path: 'ios-app',
                    element: <MobileAppRedirect />,
                },
                {
                    path: 'android-app',
                    element: <MobileAppRedirect />,
                },
                {
                    path: 'join-session',
                    element: <GuestJoinSession />,
                },
                {
                    path: 'app-redirect',
                    element: <AppRedirect />,
                },
                {
                    path: 'group-session',
                    element: <MobileGroupSession />,
                },
                {
                    path: 'phone-call/:type',
                    element: <PhoneCallCallback />,
                },
                {
                    path: 'stripe/onboarding/complete',
                    element: (
                        <RoleBasedGuard roles={[UserRole.PROVIDER]}>
                            <StripeOnboardingComplete />,
                        </RoleBasedGuard>
                    ),
                },
            ],
        },

        // Main Routes
        {
            path: 'main',
            element: (
                <AuthGuard>
                    <MainLayout />
                    <StripeRedirectModal />
                </AuthGuard>
            ),
            children: [
                { element: <Navigate to={DEFAULT_PATH_AFTER_LOGIN} replace />, index: true },
                { path: 'dashboard', element: <Dashboard /> },
                {
                    path: 'live-session',
                    element: <LiveSession />,
                },
                {
                    path: 'group-session',
                    element: (
                        <RoleBasedGuard
                            roles={[UserRole.PROVIDER, UserRole.CLIENT, ...ADMIN_ROLES]}
                        >
                            <GroupSession />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'appointments',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            <Appointments />
                        </CountryFeatureGuard>
                    ),
                },
                {
                    path: 'appointments/appointment/:appointmentId',
                    element: (
                        // Network-partner admins land here after scheduling for a client.
                        <RoleBasedGuard
                            roles={[
                                UserRole.CLIENT,
                                UserRole.PROVIDER,
                                UserRole.NETWORK_PARTNER_ADMIN,
                            ]}
                        >
                            <AppointmentDetails />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'appointments/reschedule/:appointmentId',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <RescheduleAppointment />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'claims',
                    element: (
                        <RoleBasedGuard
                            roles={[
                                ...ROLE_PERMISSION_MAP[ClientPermission.READ_CLAIMS],
                                UserRole.SUPER_ADMIN,
                            ]}
                        >
                            <Claims />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'clients',
                    element: (
                        <RoleBasedGuard roles={[UserRole.PROVIDER, ...ADMIN_ROLES]}>
                            <Clients />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'groups',
                    element: (
                        <RoleBasedGuard
                            roles={[UserRole.CLIENT, UserRole.PROVIDER, ...ADMIN_ROLES]}
                        >
                            <Groups />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'groups/new',
                    element: (
                        <RoleBasedGuard roles={[UserRole.PROVIDER, ...ADMIN_ROLES]}>
                            <NewGroup />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'groups/group/:groupId',
                    element: (
                        <RoleBasedGuard
                            roles={[UserRole.CLIENT, UserRole.PROVIDER, ...ADMIN_ROLES]}
                        >
                            <GroupDetails />
                        </RoleBasedGuard>
                    ),
                },

                {
                    path: 'provider-feedback-log',
                    element: (
                        <RoleBasedGuard roles={ADMIN_ROLES}>
                            <ProviderFeedbackLog />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'escalations',
                    element: (
                        <RoleBasedGuard roles={[UserRole.SUPER_ADMIN, UserRole.CC_MANAGER]}>
                            <Escalations />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'messages',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            <RoleBasedGuard roles={[UserRole.PROVIDER, UserRole.CLIENT]}>
                                <ClientMessagesGuard>
                                    <Messages />
                                </ClientMessagesGuard>
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },
                {
                    path: 'clients/new',
                    element: (
                        <RoleBasedGuard
                            roles={[
                                UserRole.SUPER_ADMIN,
                                UserRole.CC_MANAGER,
                                UserRole.CARE_COORDINATOR,
                                UserRole.ORGANIZATION_ADMIN,
                                ...PAYER_ROLES,
                            ]}
                        >
                            <NewClient />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'journals',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <Journals />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'journals/new',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <NewJournal />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'providers',
                    element: (
                        <RoleBasedGuard roles={[...ADMIN_ROLES, ...PAYER_ROLES, UserRole.CLIENT]}>
                            <Providers />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'providers/new',
                    element: (
                        <RoleBasedGuard
                            roles={[
                                UserRole.SUPER_ADMIN,
                                UserRole.RECRUITER,
                                UserRole.CLINICAL_SUPERVISOR,
                            ]}
                        >
                            <NewProvider />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'providers/provider/:providerUserId',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <ProviderDetails />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'providers/book/:providerId',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <BookProvider />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'availability',
                    element: (
                        <RoleBasedGuard roles={[UserRole.PROVIDER]}>
                            <Availability />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'assessments',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <Assessments />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'assessments/new',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <NewAssessment />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'assignments',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            <RoleBasedGuard roles={[UserRole.CLIENT]}>
                                <Assignments />
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },
                {
                    path: 'assignments/completed',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            <RoleBasedGuard roles={[UserRole.CLIENT]}>
                                <Assignments />
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },
                {
                    path: 'assignments/assigned',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            <RoleBasedGuard roles={[UserRole.CLIENT]}>
                                <Assignments />
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },
                {
                    path: 'assignments/:slug',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            {/* Clients complete assignments here; providers/admins
                                use the same route as a read-only preview from the
                                Assignment Library. */}
                            <RoleBasedGuard
                                roles={[UserRole.CLIENT, UserRole.PROVIDER, ...ADMIN_ROLES]}
                            >
                                <AssignmentSlugPage />
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },
                {
                    path: 'assignments/detail/:assignmentId',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            {/* Allow clients, providers, and admins to view assignment details */}
                            <RoleBasedGuard
                                roles={[UserRole.CLIENT, UserRole.PROVIDER, ...ADMIN_ROLES]}
                            >
                                <AssignmentDetail />
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },
                {
                    // Provider / care coordinator / admin catalog of assignments.
                    // Uses a distinct path (not under `assignments/`) so it does
                    // not collide with the client `assignments/:slug` route.
                    path: 'assignment-library',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            <RoleBasedGuard roles={[UserRole.PROVIDER, ...ADMIN_ROLES]}>
                                <AssignmentLibrary />
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },
                {
                    path: 'plans',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            <RoleBasedGuard roles={[UserRole.CLIENT]}>
                                <Plans />
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },

                // Apparently optional param syntax doesn't work with react-router v6.4?
                {
                    path: 'book/:clientUserId',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            <RoleBasedGuard
                                roles={[
                                    UserRole.PROVIDER,
                                    UserRole.CLIENT,
                                    ...PAYER_ROLES,
                                    ...ADMIN_ROLES,
                                ]}
                            >
                                <Book />
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },
                {
                    path: 'book',
                    element: (
                        <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                            <RoleBasedGuard
                                roles={[
                                    UserRole.PROVIDER,
                                    UserRole.CLIENT,
                                    ...PAYER_ROLES,
                                    ...ADMIN_ROLES,
                                ]}
                            >
                                <Book />
                            </RoleBasedGuard>
                        </CountryFeatureGuard>
                    ),
                },
                {
                    path: 'carebot',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT, ...ADMIN_ROLES]}>
                            <Carebot />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'assistant',
                    element: (
                        <RoleBasedGuard roles={[UserRole.PROVIDER]}>
                            <Assistant />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'user/:userId',
                    element: (
                        <RoleBasedGuard
                            roles={[...ADMIN_ROLES, UserRole.ORGANIZATION_ADMIN, ...PAYER_ROLES]}
                        >
                            <User />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'account',
                    element: (
                        <RoleBasedGuard>
                            <Account />
                        </RoleBasedGuard>
                    ),
                },
                { path: 'learn', element: <Learn /> },
                { path: 'community', element: <Community /> },
                {
                    path: 'payer/settings',
                    element: (
                        <RoleBasedGuard roles={[UserRole.NETWORK_PARTNER_ADMIN]}>
                            <PayerSettings />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'organization',
                    children: [
                        {
                            path: 'members',
                            element: (
                                <RoleBasedGuard roles={[UserRole.ORGANIZATION_ADMIN]}>
                                    <OrganizationMembers />
                                </RoleBasedGuard>
                            ),
                        },
                        {
                            path: 'settings',
                            element: (
                                <RoleBasedGuard roles={[UserRole.ORGANIZATION_ADMIN]}>
                                    <OrganizationSettings />
                                </RoleBasedGuard>
                            ),
                        },
                    ],
                },
                {
                    path: 'administrative',
                    children: [
                        {
                            path: 'settings',
                            element: (
                                <RoleBasedGuard
                                    roles={[
                                        UserRole.SUPER_ADMIN,
                                        UserRole.BILLING_ADMIN,
                                        UserRole.CC_MANAGER,
                                    ]}
                                >
                                    <AdminSettings />
                                </RoleBasedGuard>
                            ),
                        },
                        {
                            path: 'organization/:organizationId',
                            element: (
                                <RoleBasedGuard roles={[UserRole.SUPER_ADMIN]}>
                                    <Organization />
                                </RoleBasedGuard>
                            ),
                        },
                        {
                            path: 'organizations',
                            element: (
                                <RoleBasedGuard roles={[UserRole.SUPER_ADMIN]}>
                                    <Organizations />
                                </RoleBasedGuard>
                            ),
                        },
                        {
                            path: 'payouts',
                            element: (
                                <RoleBasedGuard roles={[UserRole.SUPER_ADMIN]}>
                                    <Payouts />
                                </RoleBasedGuard>
                            ),
                        },
                        {
                            path: 'users',
                            element: (
                                <RoleBasedGuard roles={[UserRole.SUPER_ADMIN]}>
                                    <AdminUsers />
                                </RoleBasedGuard>
                            ),
                        },
                        {
                            path: 'audit',
                            element: (
                                <RoleBasedGuard roles={[UserRole.SUPER_ADMIN]}>
                                    <Audit />
                                </RoleBasedGuard>
                            ),
                        },
                        {
                            path: 'fax-management',
                            element: (
                                <RoleBasedGuard
                                    roles={[UserRole.SUPER_ADMIN, UserRole.BILLING_ADMIN]}
                                >
                                    <FaxManagement />
                                </RoleBasedGuard>
                            ),
                        },
                        // {
                        //     path: 'global',
                        //     element: (
                        //         <RoleBasedGuard roles={[UserRole.SUPER_ADMIN]}>
                        //             <AdminSettings />
                        //         </RoleBasedGuard>
                        //     ),
                        // },
                    ],
                },
                {
                    path: 'members',
                    element: (
                        <RoleBasedGuard roles={PAYER_ROLES}>
                            <Members />
                        </RoleBasedGuard>
                    ),
                },
                { path: 'provider-knowledge-base', element: <ProviderKnowledgeBase /> },
                { path: 'permission-denied', element: <PermissionDenied /> },
                {
                    path: 'care-circle',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <CareCircle />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'care-circle-faq',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <CareCircleFAQ />
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'billing-and-subscription',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT]}>
                            <CountryFeatureGuard features={[COUNTRY_SPECIFIC_FEATURES.THERAPY]}>
                                <BillingAndSubscription />
                            </CountryFeatureGuard>
                        </RoleBasedGuard>
                    ),
                },
                {
                    path: 'notifications',
                    element: (
                        <RoleBasedGuard roles={[UserRole.CLIENT, UserRole.PROVIDER]}>
                            <Notifications />
                        </RoleBasedGuard>
                    )
                }
            ],
        },

        {
            path: 'main/onboarding',
            element: (
                <AuthGuard>
                    <RoleBasedGuard roles={[UserRole.CLIENT]}>
                        <RegistrationCompletionGuard
                            registrationCompleted={false}
                            allowIncompleteIntake
                        >
                            <Onboarding />
                        </RegistrationCompletionGuard>
                    </RoleBasedGuard>
                </AuthGuard>
            ),
        },

        // Static Routes
        {
            path: '*',
            element: <LogoOnlyLayout />,
            children: [
                { path: 'coming-soon', element: <ComingSoon /> },
                { path: 'maintenance', element: <Maintenance /> },
                { path: '500', element: <Page500 /> },
                { path: '404', element: <Page404 /> },
                { path: '403', element: <Page403 /> },
                { path: '*', element: <Navigate to="/404" replace /> },
            ],
        },
        {
            path: '/',
            element: <Navigate to="/auth/login" replace />,
        },
        { path: '*', element: <Navigate to="/404" replace /> },
    ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ProviderRegister = Loadable(lazy(() => import('../pages/auth/ProviderRegister')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// MAGIC LINKS
const MinorConsent = Loadable(lazy(() => import('../pages/magic-links/MinorConsent')));
const EmailVerification = Loadable(lazy(() => import('../pages/magic-links/EmailVerification')));
const AppointmentRequestConfirmation = Loadable(
    lazy(() => import('../pages/magic-links/AppointmentRequestConfirmation'))
);
const AppointmentRescheduleRequestConfirmation = Loadable(
    lazy(() => import('../pages/magic-links/AppointmentRescheduleRequestConfirmation'))
);
const NewAssessment = Loadable(lazy(() => import('../pages/magic-links/NewAssessment')));
const AppDownload = Loadable(lazy(() => import('../pages/magic-links/AppDownload')));
const MobileAppRedirect = Loadable(lazy(() => import('../pages/magic-links/MobileAppRedirect')));
const AppRedirect = Loadable(lazy(() => import('../pages/magic-links/AppRedirect')));
const GuestJoinSession = Loadable(lazy(() => import('../pages/magic-links/GuestJoinSession')));
const MobileGroupSession = Loadable(lazy(() => import('../pages/magic-links/MobileGroupSession')));
const PhoneCallCallback = Loadable(lazy(() => import('../pages/magic-links/PhoneCallCallback')));
const StripeOnboardingComplete = Loadable(
    lazy(() => import('../pages/magic-links/StripeOnboardingComplete'))
);

// GENERAL
const Dashboard = Loadable(lazy(() => import('../pages/main/Dashboard')));
const LiveSession = Loadable(lazy(() => import('../contexts/live-session/LiveSession')));
const GroupSession = Loadable(lazy(() => import('../pages/main/GroupSession')));
const Appointments = Loadable(lazy(() => import('../pages/main/Appointments')));
const Claims = Loadable(lazy(() => import('../pages/main/Claims')));
const Clients = Loadable(lazy(() => import('../pages/main/Clients')));
const Groups = Loadable(lazy(() => import('../pages/main/Groups')));
const GroupDetails = Loadable(lazy(() => import('../pages/main/GroupDetails')));
const NewGroup = Loadable(lazy(() => import('../pages/main/NewGroup')));
const Messages = Loadable(lazy(() => import('../pages/main/Messages')));
const NewClient = Loadable(lazy(() => import('../pages/main/NewClient')));
const Providers = Loadable(lazy(() => import('../pages/main/Providers')));
const NewProvider = Loadable(lazy(() => import('../pages/main/NewProvider')));
const ProviderDetails = Loadable(lazy(() => import('../pages/main/ProviderDetails')));
const BookProvider = Loadable(lazy(() => import('../pages/main/BookProvider')));
const Availability = Loadable(lazy(() => import('../pages/main/Availability')));
const Assessments = Loadable(lazy(() => import('../pages/main/Assessments')));
const Book = Loadable(lazy(() => import('../pages/main/Book')));
const Carebot = Loadable(lazy(() => import('../pages/main/Carebot')));
const Assistant = Loadable(lazy(() => import('../pages/main/Assistant')));
const Journals = Loadable(lazy(() => import('../pages/main/Journals')));
const NewJournal = Loadable(lazy(() => import('../pages/main/NewJournal')));
const Plans = Loadable(lazy(() => import('../pages/main/Plans')));
const User = Loadable(lazy(() => import('../pages/main/User')));
const Assignments = Loadable(lazy(() => import('../pages/client/AssignmentsPage')));
const AssignmentDetail = Loadable(lazy(() => import('../pages/client/AssignmentDetailPage')));
const AssignmentSlugPage = Loadable(lazy(() => import('../pages/client/AssignmentSlugPage')));
const AssignmentLibrary = Loadable(lazy(() => import('../pages/main/AssignmentLibrary')));
const Account = Loadable(lazy(() => import('../pages/main/Account')));
const Learn = Loadable(lazy(() => import('../pages/main/Learn')));
const Onboarding = Loadable(lazy(() => import('../pages/main/Onboarding')));
const Community = Loadable(lazy(() => import('../pages/main/Community')));
const Members = Loadable(lazy(() => import('../pages/main/Members')));
const Notifications = Loadable(lazy(() => import('../pages/main/Notifications')));

const AppointmentDetails = Loadable(lazy(() => import('../pages/main/AppointmentDetails')));
const RescheduleAppointment = Loadable(lazy(() => import('../pages/main/RescheduleAppointment')));
const CareCircle = Loadable(lazy(() => import('../pages/main/CareCircle')));
const CareCircleFAQ = Loadable(lazy(() => import('../pages/main/CareCircleFAQ')));
const BillingAndSubscription = Loadable(
    lazy(() => import('../pages/main/BillingAndSubscriptions'))
);

// ADMIN
const Organization = Loadable(lazy(() => import('../pages/organization/Organization')));
const Organizations = Loadable(lazy(() => import('../pages/organization/Organizations')));
const AdminSettings = Loadable(lazy(() => import('../pages/main/AdminSettings')));
const Payouts = Loadable(lazy(() => import('../pages/main/Payouts')));
const AdminUsers = Loadable(lazy(() => import('../pages/main/AdminUsers')));
const Audit = Loadable(lazy(() => import('../pages/main/Audit')));
const Escalations = Loadable(lazy(() => import('../pages/main/Escalations')));
const ProviderFeedbackLog = Loadable(lazy(() => import('../pages/main/ProviderFeedbackLog')));

// PAYER
const PayerSettings = Loadable(lazy(() => import('../pages/payer/Settings')));

// ORGANIZATION
const OrganizationMembers = Loadable(lazy(() => import('../pages/organization/Members')));
const OrganizationSettings = Loadable(lazy(() => import('../pages/organization/Settings')));

// TODO: TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/main/PermissionDenied')));

// STATIC
// const About = Loadable(lazy(() => import('../pages/static/About')));
// const Contact = Loadable(lazy(() => import('../pages/static/Contact')));
// const Faqs = Loadable(lazy(() => import('../pages/static/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/static/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/static/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/static/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/static/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/static/Page404')));
